// PET-641 / PET-672 — admin / super-admin editor for the curated suggested
// questions that appear as clickable bubbles in the chat interfaces (Search
// "Ask a Question" and Roundtable "Ask the panel"). Up to 50 entries; each
// can be a question or a statement, optionally colour-tagged and assigned to
// a team / multiple teams / all teams / no teams. Admins can also multi-select
// rows and bulk-set colour or team assignment, reorder within colour groups,
// and filter the list by colour / team. Saving replaces the whole list.
//
// Lifted from the prototype's ManageQuestionsView with prod-idiom changes:
// token plumbing dropped (prod auth is httpOnly cookies via the shared api
// client), and save routed through a useMutation that INVALIDATES the
// ["suggested-questions"] query key on success — under prod's staleTime the
// chat surfaces would otherwise keep serving the stale cached list, failing
// the "visible without hard refresh" AC.
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Check,
  FileQuestion,
  GripVertical,
  Plus,
  Trash2,
  Users,
  X,
} from "lucide-react";

import { api, type ApiError, type MyTeam, type SuggestedQuestion } from "../lib/api";
import {
  colorLabel,
  colorStyle,
  isQuestionColor,
  MULTI_COLOR_SWATCH,
  PALETTE_ORDER,
  type QuestionColor,
} from "../lib/questionColors";

const MAX_QUESTIONS = 50;

type Row = SuggestedQuestion;

// Stable comparison key for dirty detection (ignores blank rows).
function serializeRows(rows: Row[]): string {
  const cleaned = rows
    .map((r) => ({
      text: r.text.trim(),
      color: r.color ?? null,
      teams: r.teams === "all" ? "all" : [...r.teams].sort(),
    }))
    .filter((r) => r.text.length > 0);
  return JSON.stringify(cleaned);
}

function newRow(): Row {
  return {
    id:
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `new-${Math.random().toString(36).slice(2)}`,
    text: "",
    color: null,
    teams: [],
  };
}

export function ManageQuestionsView() {
  const qc = useQueryClient();
  const [rows, setRows] = useState<Row[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Display-only filters. Empty sets mean "show everything". Colour values
  // are palette tokens or the "none" sentinel (uncoloured); team values are
  // team ids. Filters never mutate the underlying rows.
  const [colorFilter, setColorFilter] = useState<Set<string>>(new Set());
  const [teamFilter, setTeamFilter] = useState<Set<string>>(new Set());
  // Index of the row currently being dragged; the row hovered over as a drop
  // target. Used to reorder the flat list (which controls per-colour-group
  // order on the display surfaces, since groupByColor preserves list order).
  const dragIndexRef = useRef<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [savedNote, setSavedNote] = useState<string | null>(null);
  // Keep the loaded baseline so we can show a dirty state / reset.
  const loadedRef = useRef<string>("[]");

  // management=true → the full unfiltered list (admin management UI).
  const q = useQuery({
    queryKey: ["suggested-questions", "management"],
    queryFn: () => api.suggestedQuestions(true),
  });

  // Assignable teams for the assignment controls (reuses /teams/mine, which
  // returns all teams in the admin's scoped communities).
  const teamsQ = useQuery({
    queryKey: ["my-teams"],
    queryFn: () => api.myTeams(),
  });
  const teams = useMemo<MyTeam[]>(() => teamsQ.data ?? [], [teamsQ.data]);

  // Seed the editable rows once the list loads.
  useEffect(() => {
    if (q.data) {
      const seeded =
        q.data.questions.length > 0
          ? q.data.questions.map((r) => ({ ...r }))
          : [newRow()];
      loadedRef.current = serializeRows(q.data.questions);
      setRows(seeded);
      setSelected(new Set());
    }
  }, [q.data]);

  const saveMut = useMutation({
    mutationFn: (questions: SuggestedQuestion[]) =>
      api.setSuggestedQuestions(questions),
    onSuccess: (res) => {
      // Re-seed from the server's NORMALIZED response, not the local rows.
      loadedRef.current = serializeRows(res.questions);
      setRows(
        res.questions.length > 0
          ? res.questions.map((r) => ({ ...r }))
          : [newRow()],
      );
      setSelected(new Set());
      setSavedNote("Saved");
      // Invalidate BOTH the management list (this view) and the chat-surface
      // read (["suggested-questions"]) so Search / Roundtable bubbles refresh
      // without a hard reload.
      void qc.invalidateQueries({
        queryKey: ["suggested-questions", "management"],
      });
      void qc.invalidateQueries({ queryKey: ["suggested-questions"] });
    },
  });

  const max = q.data?.max_questions ?? MAX_QUESTIONS;
  const palette: QuestionColor[] = useMemo(() => {
    const fromServer = q.data?.colors;
    if (fromServer && fromServer.length > 0) {
      return fromServer.filter((c): c is QuestionColor =>
        (PALETTE_ORDER as string[]).includes(c),
      );
    }
    return PALETTE_ORDER;
  }, [q.data?.colors]);

  const nonBlankCount = rows.filter((r) => r.text.trim().length > 0).length;
  const dirty = serializeRows(rows) !== loadedRef.current;
  const atCap = rows.length >= max;

  const selectableIds = rows
    .filter((r) => r.text.trim().length > 0)
    .map((r) => r.id);
  const allSelected =
    selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));

  // ---- display-only filtering ----
  const filterActive = colorFilter.size > 0 || teamFilter.size > 0;

  // Blank (in-progress) rows always stay visible so a filter never hides the
  // row the admin is currently typing into. Non-blank rows must match the
  // selected colour(s) AND team(s); a question on "all teams" matches any
  // team filter.
  function rowMatchesFilter(row: Row): boolean {
    if (row.text.trim().length === 0) return true;
    const token = isQuestionColor(row.color) ? row.color : "none";
    const matchColor = colorFilter.size === 0 || colorFilter.has(token);
    const matchTeam =
      teamFilter.size === 0 ||
      row.teams === "all" ||
      (Array.isArray(row.teams) && row.teams.some((t) => teamFilter.has(t)));
    return matchColor && matchTeam;
  }

  // Keep the original index alongside each visible row so reorder, drag,
  // select, and bulk actions all operate against the full list.
  const visibleRows = rows
    .map((row, index) => ({ row, index }))
    .filter(({ row }) => rowMatchesFilter(row));
  const shownCount = visibleRows.filter(
    ({ row }) => row.text.trim().length > 0,
  ).length;

  function toggleColorFilter(value: string) {
    setColorFilter((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  }

  function toggleTeamFilter(id: string) {
    setTeamFilter((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function resetFilters() {
    setColorFilter(new Set());
    setTeamFilter(new Set());
  }

  function markChanged() {
    setSavedNote(null);
  }

  function updateRow(id: string, patch: Partial<Row>) {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    markChanged();
  }

  function addRow() {
    setRows((prev) => (prev.length >= max ? prev : [...prev, newRow()]));
    markChanged();
  }

  function removeRow(id: string) {
    setRows((prev) => {
      const next = prev.filter((r) => r.id !== id);
      return next.length > 0 ? next : [newRow()];
    });
    setSelected((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    markChanged();
  }

  // Move a row from one position to another in the flat list. Because the
  // display surfaces group by colour while preserving list order, this also
  // reorders questions within their colour group.
  function moveRow(from: number, to: number) {
    if (from === to || from < 0 || to < 0 || from >= rows.length || to >= rows.length) {
      return;
    }
    setRows((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved!);
      return next;
    });
    markChanged();
  }

  function handleDrop(targetIndex: number) {
    const from = dragIndexRef.current;
    dragIndexRef.current = null;
    setDragOverIndex(null);
    if (from == null) return;
    moveRow(from, targetIndex);
  }

  function toggleSelect(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    setSelected((prev) =>
      prev.size >= selectableIds.length ? new Set() : new Set(selectableIds),
    );
  }

  // ---- bulk actions over the current selection ----
  function bulkSetColor(color: QuestionColor | null) {
    setRows((prev) => prev.map((r) => (selected.has(r.id) ? { ...r, color } : r)));
    markChanged();
  }

  function bulkSetAllTeams() {
    setRows((prev) =>
      prev.map((r) => (selected.has(r.id) ? { ...r, teams: "all" } : r)),
    );
    markChanged();
  }

  function bulkClearTeams() {
    setRows((prev) =>
      prev.map((r) => (selected.has(r.id) ? { ...r, teams: [] } : r)),
    );
    markChanged();
  }

  function bulkAssignTeam(teamId: string) {
    setRows((prev) =>
      prev.map((r) => {
        if (!selected.has(r.id)) return r;
        if (r.teams === "all") return r; // already broader than a single team
        const set = new Set(r.teams);
        set.add(teamId);
        return { ...r, teams: [...set] };
      }),
    );
    markChanged();
  }

  function bulkUnassignTeam(teamId: string) {
    setRows((prev) =>
      prev.map((r) => {
        if (!selected.has(r.id)) return r;
        if (r.teams === "all") return r;
        return { ...r, teams: r.teams.filter((t) => t !== teamId) };
      }),
    );
    markChanged();
  }

  function save() {
    setSavedNote(null);
    const cleaned: SuggestedQuestion[] = rows
      .map((r) => ({
        id: r.id,
        text: r.text.trim(),
        color: r.color ?? null,
        teams: r.teams === "all" ? ("all" as const) : [...r.teams],
      }))
      .filter((r) => r.text.length > 0);
    saveMut.mutate(cleaned);
  }

  function reset() {
    if (q.data) {
      setRows(
        q.data.questions.length > 0
          ? q.data.questions.map((r) => ({ ...r }))
          : [newRow()],
      );
      setSelected(new Set());
      setSavedNote(null);
    }
  }

  const saving = saveMut.isPending;
  const saveError = saveMut.isError
    ? (saveMut.error as Partial<ApiError>)?.message || "Failed to save questions"
    : null;

  return (
    <div className="space-y-6">
      <div>
        <h1
          className="flex items-center gap-2 text-2xl font-semibold text-foreground"
          style={{ fontFamily: "var(--app-font-serif)" }}
        >
          <FileQuestion className="h-5 w-5 text-primary" aria-hidden="true" />
          Manage Questions
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          Curate up to {max} reusable questions or statements. They appear as
          clickable bubbles in the Search “Ask a Question” box and the Roundtable
          “Ask the panel” box — clicking one fills in the text so anyone can send
          or edit it. Tag each with a colour and choose which teams see it. Only
          admins can edit this list.
        </p>
      </div>

      <section className="rounded-lg border border-border bg-card p-5 shadow-sm">
        {q.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : q.isError ? (
          <div className="rounded border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            {(q.error as Partial<ApiError>)?.message || "Failed to load questions"}
          </div>
        ) : (
          <>
            {/* Select-all + bulk action bar */}
            <div className="mb-3 flex flex-wrap items-center gap-3 border-b border-border pb-3">
              <label className="flex cursor-pointer items-center gap-2 text-sm text-foreground">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleSelectAll}
                  disabled={selectableIds.length === 0}
                  className="h-4 w-4 rounded border-input"
                />
                Select all
              </label>
              {selected.size > 0 ? (
                <BulkBar
                  count={selected.size}
                  palette={palette}
                  teams={teams}
                  onSetColor={bulkSetColor}
                  onSetAllTeams={bulkSetAllTeams}
                  onClearTeams={bulkClearTeams}
                  onAssignTeam={bulkAssignTeam}
                  onUnassignTeam={bulkUnassignTeam}
                  onClearSelection={() => setSelected(new Set())}
                />
              ) : (
                <span className="text-xs text-muted-foreground">
                  Select questions to bulk-edit their colour or teams.
                </span>
              )}

              <FilterBar
                palette={palette}
                teams={teams}
                colorFilter={colorFilter}
                teamFilter={teamFilter}
                onToggleColor={toggleColorFilter}
                onToggleTeam={toggleTeamFilter}
                onClearColors={() => setColorFilter(new Set())}
                onClearTeams={() => setTeamFilter(new Set())}
                onReset={resetFilters}
                active={filterActive}
                shown={shownCount}
                total={nonBlankCount}
              />
            </div>

            <div className="space-y-2">
              {visibleRows.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">
                  No questions match the current filter.
                </p>
              ) : (
                visibleRows.map(({ row, index: i }) => (
                  <QuestionRow
                    key={row.id}
                    index={i}
                    total={rows.length}
                    row={row}
                    palette={palette}
                    teams={teams}
                    selected={selected.has(row.id)}
                    dragOver={dragOverIndex === i}
                    onToggleSelect={() => toggleSelect(row.id)}
                    onChangeText={(text) => updateRow(row.id, { text })}
                    onChangeColor={(color) => updateRow(row.id, { color })}
                    onChangeTeams={(teamsVal) =>
                      updateRow(row.id, { teams: teamsVal })
                    }
                    onRemove={() => removeRow(row.id)}
                    onMoveUp={() => moveRow(i, i - 1)}
                    onMoveDown={() => moveRow(i, i + 1)}
                    onDragStart={() => {
                      dragIndexRef.current = i;
                    }}
                    onDragOverRow={() => setDragOverIndex(i)}
                    onDropRow={() => handleDrop(i)}
                    onDragEnd={() => {
                      dragIndexRef.current = null;
                      setDragOverIndex(null);
                    }}
                  />
                ))
              )}
            </div>

            <div className="mt-3 flex items-center gap-3">
              <button
                type="button"
                onClick={addRow}
                disabled={atCap}
                className="hover-elevate inline-flex items-center gap-1.5 rounded border border-border bg-background px-3 py-2 text-sm font-medium text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Plus className="h-4 w-4" aria-hidden="true" />
                Add question
              </button>
              <span className="text-xs text-muted-foreground">
                {nonBlankCount}/{max} used
              </span>
            </div>

            {saveError ? (
              <div className="mt-3 rounded border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                {saveError}
              </div>
            ) : null}

            <div className="mt-4 flex items-center gap-3 border-t border-border pt-4">
              <button
                type="button"
                onClick={save}
                disabled={saving || !dirty}
                className="inline-flex items-center gap-1.5 rounded bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save"}
              </button>
              {dirty ? (
                <button
                  type="button"
                  onClick={reset}
                  disabled={saving}
                  className="hover-elevate inline-flex items-center gap-1.5 rounded border border-border bg-background px-4 py-2 text-sm font-medium text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Reset
                </button>
              ) : null}
              {savedNote ? (
                <span className="text-sm text-primary">{savedNote}</span>
              ) : null}
            </div>
          </>
        )}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------

function ColorPicker({
  value,
  palette,
  onChange,
}: {
  value: string | null;
  palette: QuestionColor[];
  onChange: (color: QuestionColor | null) => void;
}) {
  return (
    <div className="flex items-center gap-1" role="group" aria-label="Colour">
      <button
        type="button"
        title="No colour"
        aria-label="No colour"
        aria-pressed={value == null}
        onClick={() => onChange(null)}
        className={`flex h-5 w-5 items-center justify-center rounded-full border border-border bg-muted text-muted-foreground ${
          value == null ? "ring-2 ring-ring ring-offset-1 ring-offset-card" : ""
        }`}
      >
        <X className="h-3 w-3" aria-hidden="true" />
      </button>
      {palette.map((c) => (
        <button
          key={c}
          type="button"
          title={colorLabel(c)}
          aria-label={colorLabel(c)}
          aria-pressed={value === c}
          onClick={() => onChange(c)}
          className={`h-5 w-5 rounded-full ${colorStyle(c).swatch} ${
            value === c ? "ring-2 ring-ring ring-offset-1 ring-offset-card" : ""
          }`}
        />
      ))}
    </div>
  );
}

function teamsSummary(teams: "all" | string[], teamList: MyTeam[]): string {
  if (teams === "all") return "All teams";
  if (teams.length === 0) return "No teams";
  const names = teams.map(
    (id) => teamList.find((t) => t.id === id)?.name ?? "Unknown team",
  );
  if (names.length <= 2) return names.join(", ");
  return `${names.slice(0, 2).join(", ")} +${names.length - 2}`;
}

function TeamAssign({
  value,
  teams,
  onChange,
}: {
  value: "all" | string[];
  teams: MyTeam[];
  onChange: (teams: "all" | string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const isAll = value === "all";
  const selectedIds = isAll ? new Set<string>() : new Set(value);

  function toggleTeam(id: string) {
    if (value === "all") {
      onChange([id]);
      return;
    }
    const next = new Set(value);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange([...next]);
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="hover-elevate inline-flex max-w-[12rem] items-center gap-1.5 truncate rounded-md border border-border bg-background px-2.5 py-1.5 text-xs font-medium text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Users className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <span className="truncate">{teamsSummary(value, teams)}</span>
      </button>
      {open ? (
        <div className="absolute right-0 z-20 mt-1 w-60 rounded-md border border-border bg-popover p-1 shadow-md">
          <button
            type="button"
            onClick={() => {
              onChange("all");
              setOpen(false);
            }}
            className="hover-elevate flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-xs"
          >
            All teams
            {isAll ? <Check className="h-3.5 w-3.5" aria-hidden="true" /> : null}
          </button>
          <button
            type="button"
            onClick={() => onChange([])}
            className="hover-elevate flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-xs"
          >
            No teams
            {!isAll && selectedIds.size === 0 ? (
              <Check className="h-3.5 w-3.5" aria-hidden="true" />
            ) : null}
          </button>
          <div className="my-1 border-t border-border" />
          <div className="max-h-48 overflow-y-auto">
            {teams.length === 0 ? (
              <p className="px-2 py-1.5 text-xs text-muted-foreground">
                No teams available.
              </p>
            ) : (
              teams.map((t) => (
                <label
                  key={t.id}
                  className="hover-elevate flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-xs"
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.has(t.id)}
                    onChange={() => toggleTeam(t.id)}
                    className="h-3.5 w-3.5 rounded border-input"
                  />
                  <span className="truncate">{t.name}</span>
                  <span className="ml-auto truncate text-[10px] text-muted-foreground">
                    {t.community_name}
                  </span>
                </label>
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function QuestionRow({
  index,
  total,
  row,
  palette,
  teams,
  selected,
  dragOver,
  onToggleSelect,
  onChangeText,
  onChangeColor,
  onChangeTeams,
  onRemove,
  onMoveUp,
  onMoveDown,
  onDragStart,
  onDragOverRow,
  onDropRow,
  onDragEnd,
}: {
  index: number;
  total: number;
  row: Row;
  palette: QuestionColor[];
  teams: MyTeam[];
  selected: boolean;
  dragOver: boolean;
  onToggleSelect: () => void;
  onChangeText: (text: string) => void;
  onChangeColor: (color: QuestionColor | null) => void;
  onChangeTeams: (teams: "all" | string[]) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDragStart: () => void;
  onDragOverRow: () => void;
  onDropRow: () => void;
  onDragEnd: () => void;
}) {
  function handleKeyDown(e: KeyboardEvent<HTMLButtonElement>) {
    if (e.key === "ArrowUp") {
      e.preventDefault();
      onMoveUp();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      onMoveDown();
    }
  }

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        onDragOverRow();
      }}
      onDrop={(e) => {
        e.preventDefault();
        onDropRow();
      }}
      className={`rounded-md border bg-background p-2 ${
        dragOver ? "border-primary ring-1 ring-primary" : "border-border"
      }`}
    >
      <div className="flex items-center gap-2">
        <button
          type="button"
          draggable
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onKeyDown={handleKeyDown}
          aria-label={`Reorder question ${index + 1} of ${total}. Use arrow keys to move up or down.`}
          className="hover-elevate shrink-0 cursor-grab rounded-md p-1 text-muted-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring active:cursor-grabbing"
        >
          <GripVertical className="h-4 w-4" aria-hidden="true" />
        </button>
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggleSelect}
          aria-label={`Select question ${index + 1}`}
          className="h-4 w-4 shrink-0 rounded border-input"
        />
        <span className="w-6 shrink-0 text-right text-xs text-muted-foreground">
          {index + 1}.
        </span>
        <input
          type="text"
          value={row.text}
          onChange={(e) => onChangeText(e.target.value)}
          maxLength={300}
          placeholder="e.g. What were the main objections?"
          className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove question ${index + 1}`}
          className="hover-elevate rounded-md p-2 text-muted-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Trash2 className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-3 pl-12">
        <ColorPicker value={row.color} palette={palette} onChange={onChangeColor} />
        <TeamAssign value={row.teams} teams={teams} onChange={onChangeTeams} />
      </div>
    </div>
  );
}

function BulkBar({
  count,
  palette,
  teams,
  onSetColor,
  onSetAllTeams,
  onClearTeams,
  onAssignTeam,
  onUnassignTeam,
  onClearSelection,
}: {
  count: number;
  palette: QuestionColor[];
  teams: MyTeam[];
  onSetColor: (color: QuestionColor | null) => void;
  onSetAllTeams: () => void;
  onClearTeams: () => void;
  onAssignTeam: (teamId: string) => void;
  onUnassignTeam: (teamId: string) => void;
  onClearSelection: () => void;
}) {
  const [teamMenu, setTeamMenu] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node))
        setTeamMenu(false);
    }
    if (teamMenu) document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [teamMenu]);

  return (
    <div className="flex flex-wrap items-center gap-3">
      <span className="text-xs font-medium text-foreground">{count} selected</span>

      <div className="flex items-center gap-1.5">
        <span className="text-xs text-muted-foreground">Colour:</span>
        <ColorPicker value={null} palette={palette} onChange={onSetColor} />
      </div>

      <div className="relative" ref={ref}>
        <button
          type="button"
          onClick={() => setTeamMenu((o) => !o)}
          className="hover-elevate inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1.5 text-xs font-medium text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Users className="h-3.5 w-3.5" aria-hidden="true" />
          Teams
        </button>
        {teamMenu ? (
          <div className="absolute left-0 z-20 mt-1 w-64 rounded-md border border-border bg-popover p-1 shadow-md">
            <button
              type="button"
              onClick={onSetAllTeams}
              className="hover-elevate block w-full rounded px-2 py-1.5 text-left text-xs"
            >
              Set to all teams
            </button>
            <button
              type="button"
              onClick={onClearTeams}
              className="hover-elevate block w-full rounded px-2 py-1.5 text-left text-xs"
            >
              Clear all teams (unassign)
            </button>
            <div className="my-1 border-t border-border" />
            <p className="px-2 py-1 text-[10px] uppercase tracking-wide text-muted-foreground">
              Assign / unassign a team
            </p>
            <div className="max-h-48 overflow-y-auto">
              {teams.length === 0 ? (
                <p className="px-2 py-1.5 text-xs text-muted-foreground">
                  No teams available.
                </p>
              ) : (
                teams.map((t) => (
                  <div
                    key={t.id}
                    className="flex items-center gap-1 rounded px-2 py-1 text-xs"
                  >
                    <span className="flex-1 truncate" title={t.name}>
                      {t.name}
                    </span>
                    <button
                      type="button"
                      onClick={() => onAssignTeam(t.id)}
                      className="hover-elevate rounded border border-border px-1.5 py-0.5"
                    >
                      Add
                    </button>
                    <button
                      type="button"
                      onClick={() => onUnassignTeam(t.id)}
                      className="hover-elevate rounded border border-border px-1.5 py-0.5"
                    >
                      Remove
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        ) : null}
      </div>

      <button
        type="button"
        onClick={onClearSelection}
        className="text-xs text-muted-foreground underline-offset-2 hover:underline"
      >
        Clear selection
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------

// Right-aligned, display-only filter controls: pick one or more colours and/or
// teams to narrow the visible question list. Empty selections show everything.
function FilterBar({
  palette,
  teams,
  colorFilter,
  teamFilter,
  onToggleColor,
  onToggleTeam,
  onClearColors,
  onClearTeams,
  onReset,
  active,
  shown,
  total,
}: {
  palette: QuestionColor[];
  teams: MyTeam[];
  colorFilter: Set<string>;
  teamFilter: Set<string>;
  onToggleColor: (value: string) => void;
  onToggleTeam: (id: string) => void;
  onClearColors: () => void;
  onClearTeams: () => void;
  onReset: () => void;
  active: boolean;
  shown: number;
  total: number;
}) {
  const [colorMenu, setColorMenu] = useState(false);
  const [teamMenu, setTeamMenu] = useState(false);
  const colorRef = useRef<HTMLDivElement | null>(null);
  const teamRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (colorRef.current && !colorRef.current.contains(e.target as Node))
        setColorMenu(false);
      if (teamRef.current && !teamRef.current.contains(e.target as Node))
        setTeamMenu(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const colorLabelText =
    colorFilter.size === 0
      ? "All colours"
      : `${colorFilter.size} colour${colorFilter.size === 1 ? "" : "s"}`;
  const teamLabelText =
    teamFilter.size === 0
      ? "All teams"
      : `${teamFilter.size} team${teamFilter.size === 1 ? "" : "s"}`;

  return (
    <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
      {active ? (
        <span className="text-xs text-muted-foreground">
          Showing {shown} of {total}
        </span>
      ) : null}

      {/* Colour filter */}
      <div className="relative" ref={colorRef}>
        <button
          type="button"
          onClick={() => {
            setColorMenu((o) => !o);
            setTeamMenu(false);
          }}
          className={`hover-elevate inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
            colorFilter.size > 0
              ? "border-primary bg-primary/5 text-foreground"
              : "border-border bg-background text-foreground"
          }`}
        >
          <span
            className={`h-3 w-3 rounded-full border border-border ${MULTI_COLOR_SWATCH}`}
            aria-hidden="true"
          />
          {colorLabelText}
        </button>
        {colorMenu ? (
          <div className="absolute right-0 z-20 mt-1 w-52 rounded-md border border-border bg-popover p-1 shadow-md">
            <button
              type="button"
              onClick={onClearColors}
              disabled={colorFilter.size === 0}
              className="hover-elevate block w-full rounded px-2 py-1.5 text-left text-xs disabled:cursor-not-allowed disabled:opacity-50"
            >
              All colours
            </button>
            <div className="my-1 border-t border-border" />
            <div className="max-h-56 overflow-y-auto">
              <FilterColorOption
                value="none"
                label="No colour"
                swatch="bg-muted"
                checked={colorFilter.has("none")}
                onToggle={onToggleColor}
              />
              {palette.map((c) => (
                <FilterColorOption
                  key={c}
                  value={c}
                  label={colorLabel(c)}
                  swatch={colorStyle(c).swatch}
                  checked={colorFilter.has(c)}
                  onToggle={onToggleColor}
                />
              ))}
            </div>
          </div>
        ) : null}
      </div>

      {/* Team filter */}
      <div className="relative" ref={teamRef}>
        <button
          type="button"
          onClick={() => {
            setTeamMenu((o) => !o);
            setColorMenu(false);
          }}
          className={`hover-elevate inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
            teamFilter.size > 0
              ? "border-primary bg-primary/5 text-foreground"
              : "border-border bg-background text-foreground"
          }`}
        >
          <Users className="h-3.5 w-3.5" aria-hidden="true" />
          {teamLabelText}
        </button>
        {teamMenu ? (
          <div className="absolute right-0 z-20 mt-1 w-60 rounded-md border border-border bg-popover p-1 shadow-md">
            <button
              type="button"
              onClick={onClearTeams}
              disabled={teamFilter.size === 0}
              className="hover-elevate block w-full rounded px-2 py-1.5 text-left text-xs disabled:cursor-not-allowed disabled:opacity-50"
            >
              All teams
            </button>
            <div className="my-1 border-t border-border" />
            <div className="max-h-56 overflow-y-auto">
              {teams.length === 0 ? (
                <p className="px-2 py-1.5 text-xs text-muted-foreground">
                  No teams available.
                </p>
              ) : (
                teams.map((t) => (
                  <label
                    key={t.id}
                    className="hover-elevate flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-xs"
                  >
                    <input
                      type="checkbox"
                      checked={teamFilter.has(t.id)}
                      onChange={() => onToggleTeam(t.id)}
                      className="h-3.5 w-3.5 rounded border-input"
                    />
                    <span className="truncate">{t.name}</span>
                    <span className="ml-auto truncate text-[10px] text-muted-foreground">
                      {t.community_name}
                    </span>
                  </label>
                ))
              )}
            </div>
          </div>
        ) : null}
      </div>

      {active ? (
        <button
          type="button"
          onClick={onReset}
          className="hover-elevate inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium text-muted-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
          Reset filter
        </button>
      ) : null}
    </div>
  );
}

function FilterColorOption({
  value,
  label,
  swatch,
  checked,
  onToggle,
}: {
  value: string;
  label: string;
  swatch: string;
  checked: boolean;
  onToggle: (value: string) => void;
}) {
  return (
    <label className="hover-elevate flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-xs">
      <input
        type="checkbox"
        checked={checked}
        onChange={() => onToggle(value)}
        className="h-3.5 w-3.5 rounded border-input"
      />
      <span
        className={`h-4 w-4 rounded-full border border-border ${swatch}`}
        aria-hidden="true"
      />
      <span className="truncate">{label}</span>
    </label>
  );
}
